import { jsx as _jsx } from "react/jsx-runtime";
import { createModuleContext, getArtisModule } from "@artis/sdk";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { ModuleHostContext } from "./moduleLifecycle.js";
import { isReactArtisModule } from "./reactArtisModule.js";
/**
 * The Pane's host for its content. Looks up the registered ArtisModule by `content`.
 * React modules render in-tree (context preserved) and drive their lifecycle through the
 * useModule* hooks; vanilla modules are driven through load/show/resize/hide/unload against
 * an owned host element. No `content` renders an empty Pane.
 */
export const ArtisModuleHost = ({ paneId, content }) => {
    const module = content ? getArtisModule(content) : undefined;
    if (!module || !content)
        return null;
    if (isReactArtisModule(module))
        return _jsx(ReactModuleHost, { paneId: paneId, type: content, Component: module.component });
    return _jsx(VanillaModuleHost, { paneId: paneId, type: content, module: module });
};
function ReactModuleHost({ paneId, type, Component }) {
    const elementRef = useRef(null);
    const resizeSubs = useRef(new Set());
    const ctx = useMemo(() => createModuleContext(type), [type]);
    useEffect(() => {
        const el = elementRef.current;
        if (!el)
            return;
        const ro = new ResizeObserver((entries) => {
            const rect = entries[0]?.contentRect;
            if (!rect)
                return;
            const size = { width: rect.width, height: rect.height };
            for (const cb of resizeSubs.current)
                cb(size);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const onResize = useCallback((cb) => {
        resizeSubs.current.add(cb);
        return () => {
            resizeSubs.current.delete(cb);
        };
    }, []);
    const host = useMemo(() => ({ paneId, elementRef, visible: true, onResize, ctx }), [paneId, onResize, ctx]);
    return (_jsx("div", { ref: elementRef, className: "w-full h-full", children: _jsx(ModuleHostContext.Provider, { value: host, children: _jsx(Component, {}) }) }));
}
function VanillaModuleHost({ paneId, type, module }) {
    const elementRef = useRef(null);
    useEffect(() => {
        const el = elementRef.current;
        if (!el)
            return;
        const slot = { id: paneId, element: el };
        let unloaded = false;
        Promise.resolve(module.load(slot, createModuleContext(type))).then(() => {
            if (!unloaded)
                module.show(slot);
        });
        const ro = new ResizeObserver((entries) => {
            const rect = entries[0]?.contentRect;
            if (rect)
                module.resize(slot, { width: rect.width, height: rect.height });
        });
        ro.observe(el);
        return () => {
            unloaded = true;
            ro.disconnect();
            module.hide(slot);
            module.unload(slot);
        };
    }, [module, paneId, type]);
    return _jsx("div", { ref: elementRef, className: "w-full h-full" });
}
//# sourceMappingURL=ArtisModuleHost.js.map